import React, { useState, useEffect, useContext, useRef } from "react";
import axiosClient from "../api/axiosClient";
import { AuthContext } from "../context/AuthContext";
import { motion } from "framer-motion";
import "../styles/AllTasks.css";

function AllTasks() {
  const { user } = useContext(AuthContext);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ✅ Audit modal state
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [auditData, setAuditData] = useState([]);
  const [selectedTask, setSelectedTask] = useState({ title: "", description: "" });

  // ✅ Status update modal state
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateTaskId, setUpdateTaskId] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [updateDescription, setUpdateDescription] = useState("");
  const [skipStatusChange, setSkipStatusChange] = useState(false);

  // ✅ List View Modal
  const [showListView, setShowListView] = useState(false);

  // ✅ Ref for auto-focus
  const descriptionInputRef = useRef(null);

  // Fetch all tasks
  const fetchTasks = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosClient.get("/tasks/");
      setTasks(res.data.data || []);
    } catch (err) {
      console.error("Error fetching tasks:", err);
      setError("Unable to load tasks. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  // ✅ Auto-focus textarea when modal opens
  useEffect(() => {
    if (showUpdateModal && descriptionInputRef.current) {
      descriptionInputRef.current.focus();
    }
  }, [showUpdateModal]);

  // ✅ Step 1: Open modal for status update (authorization check)
  const handleStatusClick = (taskId, status) => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;

    if (task.assigned_to?.toLowerCase() !== user.username?.toLowerCase()) {
      alert(
        `⚠️ You are not authorized to update this task. It is assigned to ${task.assigned_to || "another user"}.`
      );
      return;
    }

    // ❌ Removed setShowListView(false) → keep list view open
    setUpdateTaskId(taskId);
    setNewStatus(status);
    setUpdateDescription("");
    setSkipStatusChange(false);
    setShowUpdateModal(true);
  };

  // ✅ Step 2: Submit the status update
  const submitStatusUpdate = async () => {
    if (!updateDescription.trim()) {
      alert("Please enter a short description for this status update.");
      return;
    }

    try {
      const res = await axiosClient.put(`/tasks/${updateTaskId}/resolve`, {
        status: newStatus,
        resolution_description: updateDescription,
        skip_status_change: skipStatusChange,
      });

      alert(res.data.message || "Task updated successfully!");
      fetchTasks();
      closeUpdateModal();
    } catch (err) {
      console.error("Error updating task:", err);
      if (err.response) {
        const statusCode = err.response.status;
        const message =
          err.response.data?.message ||
          "Failed to update task status. Please try again.";

        if (statusCode === 403) {
          alert(`⚠️ ${message}`);
        } else {
          alert(message);
        }
      } else {
        alert("Network error. Please try again later.");
      }
    }
  };

  // ✅ Keyboard shortcut: Ctrl + Enter / Cmd + Enter
  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      submitStatusUpdate();
    }
  };

  const closeUpdateModal = () => {
    setShowUpdateModal(false);
    setUpdateTaskId(null);
    setNewStatus("");
    setUpdateDescription("");
    setSkipStatusChange(false);
  };

  // ✅ Fetch and show Audit Trail
  
  const handleAuditTrail = async (taskId) => {
  try {
    const res = await axiosClient.get(`/tasks/${taskId}/audit`);

    // ✅ Backend returns an array directly, so we use it directly
    const logs = Array.isArray(res.data.data) ? res.data.data : [];
    setAuditData(logs);

    // ✅ Find the selected task from current task list for title/description
    const selected = tasks.find((t) => t.id === taskId) || { title: "", description: "" };
    setSelectedTask(selected);

    setShowAuditModal(true);
  } catch (err) {
    console.error("Error fetching audit trail:", err);
    alert("Unable to fetch audit trail.");
  }
};




  const closeAuditModal = () => {
    setShowAuditModal(false);
    setAuditData([]);
    setSelectedTask({ title: "", description: "" });
  };

  // Priority badge color
  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "high":
        return "badge-high";
      case "medium":
        return "badge-medium";
      case "low":
        return "badge-low";
      default:
        return "badge-medium";
    }
  };

  // Status badge color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "status-pending";
      case "in progress":
        return "status-progress";
      case "completed":
        return "status-completed";
      default:
        return "status-pending";
    }
  };

  return (
    <div className="tasks-container container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="fw-bold text-primary">📋 All Tasks</h3>
        <div className="d-flex gap-2">
          <button className="btn dashboard-gradient" onClick={fetchTasks}>
            🔁 Refresh
          </button>
          <button
            className="btn dashboard-gradient"
            onClick={() => setShowListView(true)}
          >
            📄 Show List View
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"></div>
          <p className="mt-2">Loading tasks...</p>
        </div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : tasks.length === 0 ? (
        <div className="text-center text-muted">No tasks available.</div>
      ) : (
        <div className="row g-4">
          {tasks.map((task) => (
            <motion.div
              key={task.id}
              className="col-md-6 col-lg-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              whileHover={{ scale: 1.03 }}
            >
              <div className={`task-card shadow-sm ${getPriorityColor(task.priority)}`}>
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <h5 className="fw-bold mb-0">{task.title}</h5>
                  <span className={`badge ${getStatusColor(task.status)}`}>
                    {task.status}
                  </span>
                </div>
                <p className="text-muted small mb-2">{task.description}</p>
                <div className="small text-secondary mb-1">
                  <strong>Priority:</strong> {task.priority} <br />
                  <strong>Category:</strong> {task.category} <br />
                  <strong>Assigned To:</strong> {task.assigned_to || "—"} <br />
                  <strong>Due:</strong> {task.due_date || "—"}
                </div>
                <div className="d-flex justify-content-end gap-2 mt-3">
                  {task.status === "Pending" && (
                    <button
                      className="btn btn-sm btn-outline-success"
                      onClick={() => handleStatusClick(task.id, "In Progress")}
                    >
                      ▶ Start
                    </button>
                  )}
                  {task.status === "In Progress" && (
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => handleStatusClick(task.id, "Completed")}
                    >
                      ✅ Complete
                    </button>
                  )}
                  {task.status === "Completed" && (
                    <button
                      className="btn btn-sm btn-warning"
                      onClick={() => handleStatusClick(task.id, "Pending")}
                    >
                      🔄 Reopen
                    </button>
                  )}
                  <button
                    className="btn btn-sm btn-outline-info"
                    onClick={() => handleAuditTrail(task.id)}
                  >
                    🕒 Audit Trail
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* ✅ Status Update Modal (Second-level stacked) */}
      {showUpdateModal && (
        <>
          <div
            className="modal-backdrop fade show second-level"
            onClick={closeUpdateModal}
          ></div>
          <div
            className="modal fade show d-block second-level"
            tabIndex="-1"
            role="dialog"
          >
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title text-primary">
                    📝 Status Update — {newStatus}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeUpdateModal}
                  ></button>
                </div>
                <div className="modal-body">
                  <label className="form-label fw-semibold">
                    Description / Remark:
                  </label>
                  <textarea
                    ref={descriptionInputRef}
                    className="form-control"
                    rows="3"
                    value={updateDescription}
                    onChange={(e) => setUpdateDescription(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Enter update details... (Press Ctrl+Enter to submit)"
                  ></textarea>
                  <div className="form-check mt-3">
                    <input
                      type="checkbox"
                      id="skipStatusChange"
                      className="form-check-input"
                      checked={skipStatusChange}
                      onChange={(e) => setSkipStatusChange(e.target.checked)}
                    />
                    <label htmlFor="skipStatusChange" className="form-check-label">
                      Do not change status (add audit remark only)
                    </label>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={closeUpdateModal}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" onClick={submitStatusUpdate}>
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ✅ Audit Trail Modal (Second-level stacked) */}
      {showAuditModal && (
        <>
          <div
            className="modal-backdrop fade show second-level"
            onClick={closeAuditModal}
          ></div>
          <div
            className="modal fade show d-block second-level"
            tabIndex="-1"
            role="dialog"
          >
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <div>
                    <h5 className="modal-title text-primary">
                      🕒 Audit Trail — {selectedTask.title}
                    </h5>
                    {selectedTask.description && (
                      <p className="text-muted small mb-0">{selectedTask.description}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeAuditModal}
                  ></button>
                </div>
                <div className="modal-body">
                  {auditData.length === 0 ? (
                    <p className="text-muted">No audit history available.</p>
                  ) : (
                    <table className="table table-sm table-bordered">
                      <thead>
                        <tr>
                          <th>Date/Time</th>
                          <th>Status</th>
                          <th>Updated By</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {auditData.map((entry, idx) => (
                          <tr key={idx}>
                            <td>{entry.timestamp || "—"}</td>
                            <td>{entry.status || "—"}</td>
                            <td>{entry.updated_by || "—"}</td>
                            <td>{entry.description || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeAuditModal}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ✅ List View Modal */}
      {showListView && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={() => setShowListView(false)}
          ></div>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-xl" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title text-primary">📄 Task List View</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowListView(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  {tasks.length === 0 ? (
                    <p className="text-muted">No tasks available.</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover align-middle">
                        <thead className="table-light">
                          <tr>
                            <th>Title</th>
                            <th>Priority</th>
                            <th>Status</th>
                            <th>Category</th>
                            <th>Assigned To</th>
                            <th>Due Date</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tasks.map((task) => (
                            <tr key={task.id}>
                              <td className="fw-semibold">{task.title}</td>
                              <td>
                                <span className={`badge ${getPriorityColor(task.priority)}`}>
                                  {task.priority}
                                </span>
                              </td>
                              <td>
                                <span className={`badge ${getStatusColor(task.status)}`}>
                                  {task.status}
                                </span>
                              </td>
                              <td>{task.category}</td>
                              <td>{task.assigned_to || "—"}</td>
                              <td>{task.due_date || "—"}</td>
                              <td>
                                <div className="d-flex gap-1">
                                  {task.status === "Pending" && (
                                    <button
                                      className="btn btn-sm dashboard-gradient"
                                      onClick={() => handleStatusClick(task.id, "In Progress")}
                                    >
                                      ▶ Start
                                    </button>
                                  )}
                                  {task.status === "In Progress" && (
                                    <button
                                      className="btn btn-sm dashboard-gradient"
                                      onClick={() => handleStatusClick(task.id, "Completed")}
                                    >
                                      ✅ Complete
                                    </button>
                                  )}
                                  {task.status === "Completed" && (
                                    <button
                                      className="btn btn-sm dashboard-gradient"
                                      onClick={() => handleStatusClick(task.id, "Pending")}
                                    >
                                      🔄 Reopen
                                    </button>
                                  )}
                                  <button
                                    className="btn btn-sm dashboard-gradient"
                                    onClick={() => handleAuditTrail(task.id)}
                                  >
                                    🕒 Audit
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowListView(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default AllTasks;
